import {
  ContextualSeatmapData,
  Journey,
  JourneyElementSeatData,
  SeatmapSeatPropertiesSeatAvailabilityStatusEnum as SeatAvailabilityStatusEnum,
  SeatmapDeck,
  SeatmapSeatProperties,
} from '@dapi/sdk';
import {
  CombineSeatmapDeckConfig,
  createContextualSeatmapData,
  createJourneyElementSeatData,
  createSeatmapDescription,
  RecursivePartial,
  todayPlusNdays,
} from '@dapi/sdk/spec';
import {
  isMultiLegJourney,
} from '../../../mock/data/mock-journeys';

export interface SeatAvailabilityOverride {
  /** The seat number */
  seatNumber: string;

  /** The availability status to override with */
  status: SeatAvailabilityStatusEnum;

  /** Whether the seat is an exit row */
  isExitRow?: boolean;
}

/**
 * Returns the mock seatmap data for the given journey and traveler ids
 *
 * @param journey the given journey
 * @param travelerIds the list of traveler ids
 */
export function retrieveSeatMapsJourneyDriven(journey: Journey, travelerIds: string[]): ContextualSeatmapData {
  const isMultiLeg = isMultiLegJourney(journey);
  const contextualSeatmapDataTemplate: RecursivePartial<ContextualSeatmapData> = {
    flight: journey.flights[0].checkInFlight,
    ...(isMultiLeg ? {
      seatmaps: [
        createSeatmapDescription({
          template: {
            leg: {
              boardPointIataCode: 'NCE',
              offPointIataCode: 'CDG',
              scheduledArrivalDateTime: todayPlusNdays('9:20', 2),
              scheduledDepartureDateTime: todayPlusNdays('9:00', 2)
            }
          }
        }),
        createSeatmapDescription({
          template: {
            leg: {
              boardPointIataCode: 'CDG',
              offPointIataCode: 'LHR',
              scheduledArrivalDateTime: todayPlusNdays('10:00', 2),
              scheduledDepartureDateTime: todayPlusNdays('9:30', 2)
            }
          }
        })
      ]
    } : {})
  };
  const decksConfig: Partial<CombineSeatmapDeckConfig>[] = [
    {
      seatmapSeatPropertiesConfigs: [{
        travelerIds
      }]
    },
    ...(isMultiLeg ? [{
      seatmapSeatPropertiesConfigs: [{
        travelerIds
      }]
    }] : [])
  ];
  return createContextualSeatmapData({
    template: contextualSeatmapDataTemplate,
    combineSeatmapDescriptionConfig: {
      combineSeatmapDeckConfigs: decksConfig
    }
  });
}

/**
 * Assigns the provided seat to the journey elements of the given journey
 *
 * @param journey journey from which we want to
 * @param seatmap seatmap from which we want to select the seat
 * @param indexSeat index of the seat to select in the filtered list of seat (free and chargeable preference)
 * @param chargeable do we want to assign a chargeable seat (true) or not (false) or no preference (undefined)
 * @returns list of JourneyElementSeatData (one seat assigned per journeyElement in input)
 */
export function assignSeatToJourneyElements(journey: Journey, seatmap: ContextualSeatmapData, indexSeat?: number, chargeable?: boolean): JourneyElementSeatData[] {
  const decks = seatmap.seatmaps.reduce((acc: SeatmapDeck[], map) => acc.concat(map.decks || []), []);
  const seats = decks.reduce((acc: SeatmapSeatProperties[], deck) => acc.concat(deck.seats || []), []);
  const seatNumbers = seats
    .filter((seat) => seat.seatAvailabilityStatus === 'available')
    .filter((seat) =>
      (chargeable
        ? seat.travelers?.find((t) => !!t.prices)
        : (chargeable === undefined || seat.travelers === undefined || seat.travelers?.find((t) => !t.prices))
      ))
    .map((seat) => seat.seatNumber);

  return journey.journeyElements
    .filter((je) => je.journeyElement.traveler.passengerTypeCode !== 'INF')
    .map((je, index) => createJourneyElementSeatData({
      template: {
        journeyElementId: je.id,
        seatNumber: seatNumbers[((indexSeat === undefined) ? index : indexSeat)],
        isInfantOnSeat: !!je.journeyElement.traveler.accompanyingTravelerId
      }
    }));
}

/**
 * Overrides the seatmap availability for the given seat numbers and traveler ids
 *
 * @param seatmap the given seatmap
 * @param travelerIds the list of traveler ids
 * @param seatOverrides the list of seat availability overrides
 */
export function overrideSeatmapAvailability(
  seatmap: ContextualSeatmapData,
  travelerIds: string[],
  seatOverrides: SeatAvailabilityOverride[] = []
): ContextualSeatmapData {
  return {
    ...seatmap,
    seatmaps: seatmap.seatmaps.map((seatMapData) => ({
      ...seatMapData,
      decks: seatMapData.decks?.map((deck) => ({
        ...deck,
        seats: deck.seats?.map((seat) => {
          const isExitRow = !!seatOverrides.find((so) => so.seatNumber === seat.seatNumber)?.isExitRow;
          const overridenStatus = seatOverrides.find((so) => so.seatNumber === seat.seatNumber)?.status || seat.seatAvailabilityStatus;
          const updatedSeat = {
            ...seat,
            seatCharacteristicsCodes: isExitRow ? seat.seatCharacteristicsCodes : seat.seatCharacteristicsCodes?.filter((c) => c !== 'E'),
            seatAvailabilityStatus: overridenStatus
          };
          updatedSeat.travelers = updatedSeat.travelers?.length
            ? updatedSeat.travelers.map((traveler) => ({
              ...traveler,
              seatAvailabilityStatus: overridenStatus
            }))
            : travelerIds.map((tid: string) => ({
              id: tid,
              seatAvailabilityStatus: overridenStatus
            }));
          return updatedSeat;
        })
      }))
    }))
  };
}
